// ConsultaPanel.tsx
import {
  Icon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast
} from '@chakra-ui/react'
import QRCode from 'qrcode'
import React, { useRef, useState } from 'react'
import { FiAward, FiCheckCircle, FiEdit, FiEye, FiInfo, FiPrinter } from 'react-icons/fi'
import BoxSearch from '../../components/BoxSearch'
import CredentialModal from '../../components/CredentialModal'
import api from '../../utils/api'
import { imprimirEtiquetas } from '../../utils/imprimirEtiqueta'
import { formatCPF } from '../../utils/util'
import DetalhesParticipante from './DetalhesParticipante'
import { Pessoa } from './types'

declare global {
  interface Window {
    qz: any;
  }
}
export { }

/**
 * Se a pessoa já retirou a credencial no balcão, e quando.
 *
 * Check-in é a ENTREGA DA CREDENCIAL, não a entrada em sala — são coisas diferentes e o
 * balcão precisa distinguir: quem já retirou não retira de novo. Fica embaixo do nome, e
 * não num ícone, porque a pergunta é feita de relance sobre a lista inteira; num ícone o
 * operador teria de passar o mouse pessoa por pessoa.
 */
/** O dia de hoje no fuso do evento, para comparar com a data do check-in. */
const hojeNoEvento = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date())

const SituacaoCheckin: React.FC<{ pessoa: Pessoa }> = ({ pessoa }) => {
  const fez = (pessoa.checkinCount ?? 0) > 0
  const quando = pessoa.ultimoCheckin ? new Date(pessoa.ultimoCheckin) : null
  /*
    Retirou HOJE ou num dia anterior — sao situacoes opostas no balcao, e ate agora tinham
    o mesmo icone verde. Num evento de tres dias, "ja fez check-in" referente a ontem faz o
    operador deixar de entregar a credencial de hoje.
  */
  const diaDoCheckin = quando && !Number.isNaN(quando.getTime())
    ? new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(quando)
    : null
  const foiHoje = diaDoCheckin === hojeNoEvento()
  const horario =
    quando && !Number.isNaN(quando.getTime())
      ? quando.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'America/Sao_Paulo',
        })
      : null

  if (!fez) {
    return (
      <Text fontSize="xs" color="gray.500" mt={0.5}>
        Sem check-in
      </Text>
    )
  }

  return (
    <Flex align="center" gap={1.5} mt={0.5}>
      <Icon as={FiCheckCircle} color={foiHoje ? 'green.500' : 'orange.400'} boxSize={3.5} />
      <Text fontSize="xs" color={foiHoje ? 'green.700' : 'orange.700'} fontWeight="600">
        {foiHoje ? 'Check-in hoje' : 'Check-in em dia anterior'}
        {horario ? ` · ${horario}` : ''}
      </Text>
      {(pessoa.checkinCount ?? 0) > 1 && (
        <Text fontSize="xs" color="gray.500">
          ({pessoa.checkinCount}× no evento)
        </Text>
      )}
    </Flex>
  )
}

interface ConsultaPanelProps {
  searchType: string
  setSearchType: (value: string) => void
  searchValue: string
  setSearchValue: (value: string) => void
  onSearch: () => void // 📌 Passando função para chamar API
  totalRegistrations: number
  totalCheckins: number
  totalPessoasCheckinHoje: number
  totalAccesses: number
  pageSize: number
  setPageSize: (value: number) => void
  page: number
  totalPages: number
  handlePrevPage: () => void
  handleNextPage: () => void
  allData: Pessoa[] // Dados já vêm paginados da API
  handleEdit: (pessoa: Pessoa) => void
  fetchRegistrationStats: () => void
  isLoading: boolean
}

const ConsultaPanel: React.FC<ConsultaPanelProps> = ({
  searchType,
  setSearchType,
  searchValue,
  setSearchValue,
  onSearch, // 📌 Agora o `BoxSearch` chama essa função
  totalRegistrations,
  totalCheckins,
  totalPessoasCheckinHoje,
  totalAccesses,
  pageSize,
  setPageSize,
  page,
  totalPages,
  handlePrevPage,
  handleNextPage,
  allData,
  handleEdit,
  fetchRegistrationStats,
  isLoading
}) => {
  const toast = useToast()
  const confirmRef = useRef<HTMLButtonElement>(null!);
  const printConfirmRef = useRef<HTMLButtonElement>(null!);
  // Ficha do participante: os treze campos que o briefing manda exibir na conferência.
  const [pessoaDetalhe, setPessoaDetalhe] = useState<Pessoa | null>(null);
  const [isCheckinDialogOpen, setIsCheckinDialogOpen] = useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [selectedPessoa, setSelectedPessoa] = useState<Pessoa | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // 🆕 Estados para seleção múltipla
  const [selectedPessoas, setSelectedPessoas] = useState<Set<string>>(new Set());
  const [selectedPessoasCache, setSelectedPessoasCache] = useState<Map<string, Pessoa>>(new Map()); // 🆕 Cache dos dados completos
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [isBatchDialogOpen, setIsBatchDialogOpen] = useState(false);
  const [isViewSelectedOpen, setIsViewSelectedOpen] = useState(false);
  const [credentialPessoa, setCredentialPessoa] = useState<Pessoa | null>(null);
  const [isCredentialOpen, setIsCredentialOpen] = useState(false);

  const openCheckinDialog = (pessoa: Pessoa) => {
    setSelectedPessoa(pessoa);
    setIsCheckinDialogOpen(true);
  };

  const closeCheckinDialog = () => {
    setSelectedPessoa(null);
    setIsCheckinDialogOpen(false);
  };

  // 🆕 Funções para seleção múltipla
  const togglePessoaSelection = (pessoaId: string) => {
    const newSelected = new Set(selectedPessoas);
    const newCache = new Map(selectedPessoasCache);
    
    if (newSelected.has(pessoaId)) {
      newSelected.delete(pessoaId);
      newCache.delete(pessoaId);
    } else {
      newSelected.add(pessoaId);
      // Encontra os dados completos da pessoa e armazena no cache
      const pessoa = allData.find(p => p.id === pessoaId);
      if (pessoa) {
        newCache.set(pessoaId, pessoa);
      }
    }
    
    setSelectedPessoas(newSelected);
    setSelectedPessoasCache(newCache);
  };

  const toggleSelectAll = () => {
    const currentPageIds = allData.map(p => p.id!);
    const currentPageSelected = currentPageIds.filter(id => selectedPessoas.has(id));
    
    const newSelected = new Set(selectedPessoas);
    const newCache = new Map(selectedPessoasCache);
    
    if (currentPageSelected.length === allData.length) {
      // Se todos da página atual estão selecionados, desmarca todos da página atual
      currentPageIds.forEach(id => {
        newSelected.delete(id);
        newCache.delete(id);
      });
    } else {
      // Seleciona todos da página atual (mantém seleções de outras páginas)
      allData.forEach(pessoa => {
        newSelected.add(pessoa.id!);
        newCache.set(pessoa.id!, pessoa);
      });
    }
    
    setSelectedPessoas(newSelected);
    setSelectedPessoasCache(newCache);
  };

  const clearSelection = () => {
    setSelectedPessoas(new Set());
    setSelectedPessoasCache(new Map());
  };

  const getSelectedPessoas = () => {
    // Usa o cache que mantém os dados completos das pessoas selecionadas
    return Array.from(selectedPessoasCache.values());
  };

  const openBatchDialog = () => {
    if (selectedPessoas.size === 0) {
      toast({
        title: "Nenhum registro selecionado",
        description: "Selecione pelo menos um registro para continuar.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsBatchDialogOpen(true);
  };

  const closeBatchDialog = () => {
    setIsBatchDialogOpen(false);
  };

  const openViewSelected = () => {
    if (selectedPessoas.size === 0) {
      toast({
        title: "Nenhum registro selecionado",
        description: "Selecione pelo menos um registro para visualizar.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsViewSelectedOpen(true);
  };

  const closeViewSelected = () => {
    setIsViewSelectedOpen(false);
  };

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  
  const processBatchCheckin = async () => {
    const selectedItems = getSelectedPessoas();
    if (selectedItems.length === 0) return;

    setIsBatchProcessing(true);
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    try {
      toast({
        title: "Processamento em lote iniciado",
        description: `Processando ${selectedItems.length} registro(s)...`,
        status: "info",
        duration: 2000,
        isClosable: true,
      });

      // 🎯 ETAPA 1: Imprimir todas as etiquetas em uma única operação
      try {
        console.log("🖨️ Imprimindo etiquetas em lote...");
        await printBatchLabels(selectedItems);
        console.log("✅ Etiquetas enviadas para impressão!");
        
        toast({
          title: "Etiquetas enviadas!",
          description: `${selectedItems.length} etiqueta(s) enviada(s) para impressão.`,
          status: "info",
          duration: 2000,
          isClosable: true,
        });
      } catch (printError) {
        console.error("Erro na impressão em lote:", printError);
        toast({
          title: "Erro na impressão",
          description: "Houve um problema ao imprimir as etiquetas.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      }

      // 🎯 ETAPA 2: Realizar check-ins individuais
      for (let i = 0; i < selectedItems.length; i++) {
        const pessoa = selectedItems[i];

        try {
          console.log(`[${i + 1}/${selectedItems.length}] Check-in: ${pessoa.nome}`);

          if (pessoa.registrationUid) {
            await api.post('/checkin', {
              registration: pessoa.registrationUid,
              room: null,
            });
          }
          
          successCount++;
          console.log(`[${i + 1}/${selectedItems.length}] Sucesso: ${pessoa.nome}`);
        } catch (error) {
          console.error(`[${i + 1}/${selectedItems.length}] Erro no check-in ${pessoa.nome}:`, error);
          errorCount++;
          errors.push(pessoa.nome);
        }

        // Pequena pausa entre check-ins para não sobrecarregar a API
        await delay(200);
      }

      // Feedback do resultado final
      if (successCount > 0) {
        toast({
          title: `Processamento concluído!`,
          description: `${successCount} check-in(s) realizado(s) com sucesso${errorCount > 0 ? `, ${errorCount} erro(s)` : ''}.`,
          status: successCount === selectedItems.length ? "success" : "warning",
          duration: 5000,
          isClosable: true,
        });
      }

      if (errorCount === selectedItems.length) {
        toast({
          title: "Erro no processamento",
          description: "Não foi possível realizar nenhum check-in.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }

      if (errors.length > 0) {
        console.warn("Registros com erro no check-in:", errors);
      }

      // Limpar seleção e atualizar dados
      clearSelection();
      fetchRegistrationStats();
      closeBatchDialog();

      // 🆕 Limpar campo de busca e focar input após processamento em lote
      setSearchValue("");
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 500);

    } catch (error) {
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro durante o processamento em lote.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsBatchProcessing(false);
    }
  };
  const openPrintDialog = (pessoa: Pessoa) => {
    setSelectedPessoa(pessoa);
    setIsPrintDialogOpen(true);
  };

  const closePrintDialog = () => {
    setSelectedPessoa(null);
    setIsPrintDialogOpen(false);
  };

  // 🆕 Função para imprimir etiquetas em lote (todas em um único documento)
  /** Várias etiquetas num documento só, pelo mesmo caminho da individual. */
  const printBatchLabels = async (pessoas: Pessoa[]): Promise<void> => {
    const codes = pessoas
      .map(p => p.credentialCode || p.registrationUid)
      .filter(Boolean) as string[]
    if (!codes.length) throw new Error('Nenhum participante com credencial.')
    await imprimirEtiquetas(codes)
  };

  // Função separada para gerar e imprimir a etiqueta usando iframe
  /**
   * Etiqueta de um participante, renderizada pelo servidor.
   *
   * Montava o HTML aqui, em 90x50mm, com nome/empresa/cargo — campos de outro evento.
   * O crachá do SBAM é 82x30mm com nome, conselho e estado. Ver utils/imprimirEtiqueta.
   */
  const printLabel = async (pessoa: Pessoa): Promise<void> => {
    if (!pessoa.registrationUid) throw new Error('Participante sem credencial.')
    await imprimirEtiquetas([pessoa.credentialCode || pessoa.registrationUid])
  };

  const handleCheckinPrint = async () => {
    if (!selectedPessoa?.registrationUid) {
      toast({
        title: "Usuário não possui um registro de inscrição válido.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Chama a função separada para impressão da etiqueta
      await printLabel(selectedPessoa);

      // Realiza o Check-in via API
      await api.post('/checkin', {
        registration: selectedPessoa.registrationUid,
        room: null,
      });            

      toast({
        title: "Check-in realizado com sucesso! Etiqueta enviada para impressão.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Limpa o campo de pesquisa e atualiza os dados
      setSearchValue("");

      // Atualiza os dados após o check-in
      fetchRegistrationStats();

      // Após a impressão, limpa o campo de busca, reseta para a página 1 e foca o input:
      setSearchValue("");      
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 500);
    } catch (error) {
      console.error("Erro ao realizar check-in:", error);
      toast({
        title: "Erro ao registrar o check-in.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      closeCheckinDialog();
    }
  };
  const handleOnlyPrint = async () => {
    if (!selectedPessoa?.registrationUid) {
      toast({
        title: "Usuário não possui um registro de inscrição válido.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await printLabel(selectedPessoa);
      toast({
        title: "Etiqueta impressa com sucesso!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // 🆕 Limpar campo de busca e focar input após impressão
      setSearchValue("");
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 500);
    } catch (error) {
      console.error("Erro ao imprimir etiqueta:", error);
      toast({
        title: "Erro ao imprimir a etiqueta.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      closePrintDialog();
    }
  };

  const handlePrintDialogCheckin = async () => {
    if (!selectedPessoa?.registrationUid) {
      toast({
        title: "Usuário não possui um registro de inscrição válido.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await printLabel(selectedPessoa);

      await api.post('/checkin', {
        registration: selectedPessoa.registrationUid,
        room: null,
      });

      toast({
        title: "Check-in realizado com sucesso! Etiqueta enviada para impressão.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setSearchValue("");
      fetchRegistrationStats();

      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 500);
    } catch (error) {
      console.error("Erro ao realizar check-in:", error);
      toast({
        title: "Erro ao registrar o check-in.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      closePrintDialog();
    }
  };

  return (
    <>
      {/* Caixa de Diálogo de Confirmação */}
      <AlertDialog
        isOpen={isCheckinDialogOpen}
        leastDestructiveRef={confirmRef}
        onClose={closeCheckinDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Emitir etiqueta e Realizar Check-in?
            </AlertDialogHeader>
            <AlertDialogBody>
              Deseja realmente realizar o check-in e emitir a etiqueta para este usuário <strong>{selectedPessoa?.nome}</strong>?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={confirmRef} onClick={closeCheckinDialog}>
                Cancelar
              </Button>
              <Button colorScheme="green" onClick={handleCheckinPrint} ml={3} ref={confirmRef}>
                Sim
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      {/* Caixa de Diálogo para Imprimir */}
      <AlertDialog
        isOpen={isPrintDialogOpen}
        leastDestructiveRef={printConfirmRef}
        onClose={closePrintDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              O que deseja fazer?
            </AlertDialogHeader>
            <AlertDialogBody>
              <strong>{selectedPessoa?.nome}</strong><br />
              Escolha se deseja <strong>realizar o check-in e imprimir a credencial</strong> ou <strong>apenas imprimir</strong>.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={printConfirmRef} onClick={closePrintDialog}>
                Cancelar
              </Button>
              <Button colorScheme="blue" onClick={handleOnlyPrint} ml={3}>
                Apenas Imprimir
              </Button>
              <Button colorScheme="green" onClick={handlePrintDialogCheckin} ml={3}>
                Check-in + Imprimir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <Box
        bgGradient={useColorModeValue(
          'linear(to-r, white, gray.50)',
          'linear(to-r, gray.800, gray.700)'
        )}
        transition="transform 0.2s ease"
        // bg={useColorModeValue('white', 'gray.800')}
        p={6}
        borderRadius="md"
        boxShadow="md"
        w="100%"  // full width
        mx="auto"
      >
        <Flex
            direction={{ base: 'column', md: 'row' }}
            alignItems="stretch"
            gap={4}
            mb={6}
          >
            {/* BoxSearch com mais espaço */}
            <Box
              flex="2"
              p={4}
              borderRadius="md"
              boxShadow="md"
              bg={useColorModeValue('white', 'gray.800')}
              display="flex"
              flexDirection="column"
              justifyContent="center"
            >
              <BoxSearch
                ref={searchInputRef}
                searchType={searchType}
                setSearchType={setSearchType}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onSearch={onSearch}
                autoOpenScanner={false}
                onScannerOpen={() => console.log('Scanner aberto')}
                loading={false}
              />
            </Box>

            {/* Cards de totais com menos padding */}
            <Flex
              flex="1"
              direction={{ base: 'column', md: 'row' }}
              alignItems="stretch"
              gap={4}
            >
              <Box
                flex="1"
                bgGradient={useColorModeValue('linear(to-r, teal.50, blue.50)', 'linear(to-r, gray.700, gray.600)')}
                p={3}  // Menos padding
                borderRadius="md"
                textAlign="center"
                boxShadow="md"
                transition="transform 0.2s, box-shadow 0.2s"
                _hover={{ transform: 'scale(1.02)', boxShadow: 'xl' }}
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.300')}>
                  Total de Inscritos
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color={useColorModeValue('teal.800', 'teal.200')}>
                  {totalRegistrations}
                </Text>
              </Box>

              <Box
                flex="1"
                bgGradient={useColorModeValue('linear(to-r, purple.50, pink.50)', 'linear(to-r, gray.700, gray.600)')}
                p={3}
                borderRadius="md"
                textAlign="center"
                boxShadow="md"
                transition="transform 0.2s, box-shadow 0.2s"
                _hover={{ transform: 'scale(1.02)', boxShadow: 'xl' }}
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.300')}>
                  Check-in hoje
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color={useColorModeValue('purple.800', 'purple.200')}>
                  {totalPessoasCheckinHoje}
                </Text>
                {/* O acumulado do evento continua visível, em segundo plano: num evento de
                    vários dias os dois números respondem perguntas diferentes. */}
                <Text fontSize="xs" color={useColorModeValue('gray.500', 'gray.400')}>
                  {totalCheckins} no evento
                </Text>
              </Box>

              <Box
                flex="1"
                bgGradient={useColorModeValue('linear(to-r, orange.50, yellow.50)', 'linear(to-r, gray.700, gray.600)')}
                p={3}
                borderRadius="md"
                textAlign="center"
                boxShadow="md"
                transition="transform 0.2s, box-shadow 0.2s"
                _hover={{ transform: 'scale(1.02)', boxShadow: 'xl' }}
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.300')}>
                  Total de Acessos
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color={useColorModeValue('orange.800', 'orange.200')}>
                  {totalAccesses}
                </Text>
              </Box>
            </Flex>
          </Flex>
        {/* 🆕 Controles de seleção múltipla */}
        <Flex 
          mb={4} 
          p={3} 
          bg={useColorModeValue('blue.50', 'blue.900')} 
          borderRadius="md" 
          align="center" 
          justify="space-between"
          wrap="wrap"
          gap={3}
        >
          <Flex align="center" gap={3}>
            <Checkbox
              isChecked={allData.length > 0 && allData.every(p => selectedPessoas.has(p.id!))}
              isIndeterminate={allData.some(p => selectedPessoas.has(p.id!)) && !allData.every(p => selectedPessoas.has(p.id!))}
              onChange={toggleSelectAll}
              colorScheme="blue"
            >
              Selecionar todos ({allData.length})
            </Checkbox>
            
            {selectedPessoas.size > 0 && (
              <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>
                {selectedPessoas.size} selecionado(s) de {allData.length} total
              </Badge>
            )}
          </Flex>

          <Flex gap={2}>
            {selectedPessoas.size > 0 && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  onClick={clearSelection}
                >
                  Limpar Seleção
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  onClick={openViewSelected}
                  leftIcon={<FiEye />}
                >
                  Mostrar Selecionados
                </Button>
                <Button
                  size="sm"
                  colorScheme="green"
                  onClick={openBatchDialog}
                  leftIcon={<FiCheckCircle />}
                  isLoading={isBatchProcessing}
                >
                  Processar Selecionados ({selectedPessoas.size})
                </Button>
              </>
            )}
          </Flex>
        </Flex>

        <Box position="relative">
          <Table variant="striped" colorScheme="gray" size="md">
            <Thead bg={useColorModeValue('gray.200', 'gray.800')}>
              <Tr>
                <Th
                  textAlign="center"
                  width="60px"
                  px={2}
                  py={3}
                  fontSize="md"
                  fontWeight="semibold"
                  borderBottom="2px"
                  borderColor={useColorModeValue('gray.300', 'gray.600')}
                >
                  <Checkbox
                    isChecked={allData.length > 0 && allData.every(p => selectedPessoas.has(p.id!))}
                    isIndeterminate={allData.some(p => selectedPessoas.has(p.id!)) && !allData.every(p => selectedPessoas.has(p.id!))}
                    onChange={toggleSelectAll}
                    colorScheme="blue"
                  />
                </Th>
                <Th
                  textAlign="left"
                  px={4}
                  py={3}
                  fontSize="md"
                  fontWeight="semibold"
                  borderBottom="2px"
                  borderColor={useColorModeValue('gray.300', 'gray.600')}
                >
                  Nome
                </Th>
                <Th
                  textAlign="left"
                  px={4}
                  py={3}
                  fontSize="md"
                  fontWeight="semibold"
                  borderBottom="2px"
                  borderColor={useColorModeValue('gray.300', 'gray.600')}
                >
                  CPF/Documento
                </Th>
                <Th
                  textAlign="left"
                  px={4}
                  py={3}
                  fontSize="md"
                  fontWeight="semibold"
                  borderBottom="2px"
                  borderColor={useColorModeValue('gray.300', 'gray.600')}
                >
                  E-mail
                </Th>
                <Th
                  textAlign="center"
                  px={4}
                  py={3}
                  fontSize="md"
                  fontWeight="semibold"
                  borderBottom="2px"
                  borderColor={useColorModeValue('gray.300', 'gray.600')}
                >
                  Ações
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {allData.map(pessoa => (
                <Tr key={pessoa.id}>
                  <Td textAlign="center" px={2}>
                    <Checkbox
                      isChecked={selectedPessoas.has(pessoa.id!)}
                      onChange={() => togglePessoaSelection(pessoa.id!)}
                      colorScheme="blue"
                    />
                  </Td>
                  <Td>
                    <Text>{pessoa.nome || "[sem nome]"}</Text>
                    <SituacaoCheckin pessoa={pessoa} />
                  </Td>
                  <Td>{formatCPF(pessoa.cpf)}</Td>
                  <Td>{pessoa.email || "-"}</Td>
                  <Td>
                    <Flex justify="space-between" align="center">
                      <Flex gap={2}>
                        <Tooltip label="Ver todos os dados do participante" hasArrow>
                          <IconButton
                            aria-label="Ver dados do participante"
                            icon={<FiInfo />}
                            variant="outline"
                            colorScheme="teal"
                            size="sm"
                            onClick={() => setPessoaDetalhe(pessoa)}
                          />
                        </Tooltip>
                        <IconButton
                          aria-label="Editar"
                          icon={<FiEdit />}
                          variant="outline"
                          colorScheme="blue"
                          size="sm"
                          onClick={() => handleEdit(pessoa)}
                        />
                        <IconButton
                          aria-label="Imprimir Etiqueta"
                          icon={<FiPrinter />}
                          variant="outline"
                          colorScheme="orange"
                          size="sm"
                          onClick={() => openPrintDialog(pessoa)}
                        />
                        <Tooltip label="Credencial não disponível para esse evento" hasArrow>
                          <Box>
                            <IconButton
                              aria-label="Ver Credencial"
                              icon={<FiAward />}
                              variant="outline"
                              colorScheme="purple"
                              size="sm"
                              isDisabled
                            />
                          </Box>
                        </Tooltip>
                      </Flex>
                      <IconButton
                        aria-label="Realizar Check-in"
                        icon={<FiCheckCircle />}
                        variant="solid"
                        colorScheme="green"
                        size="md" // 🔹 Levemente maior
                        ml={6}    // 🔹 Espaço maior à esquerda
                        onClick={() => openCheckinDialog(pessoa)}
                        boxShadow="md"
                      />
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {/* Loading Overlay */}
          {isLoading && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="whiteAlpha.700"
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex={10}
              backdropFilter="blur(2px)"
            >
              <Spinner size="xl" color="blue.500" thickness="4px" />
            </Box>
          )}
        </Box>
        <Flex mt={4} align="center" justify="center" gap={4}>
          <Button onClick={handlePrevPage} isDisabled={page === 1 || totalPages === 0 || isLoading} colorScheme="blue" variant="outline">
            Anterior
          </Button>
          <Text fontSize="md">Página {page} de {totalPages}</Text>
          <Button onClick={handleNextPage} isDisabled={page >= totalPages || totalPages === 0 || isLoading} colorScheme="blue" variant="outline">
            Próximo
          </Button>
        </Flex>
      </Box>

      {/* 🆕 Modal para visualizar registros selecionados */}
      <Modal isOpen={isViewSelectedOpen} onClose={closeViewSelected} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex alignItems="center" gap={3}>
              <Text>Registros Selecionados</Text>
              <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                {selectedPessoas.size} item(s)
              </Badge>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <Box maxHeight="400px" overflowY="auto">
              <Table variant="simple" size="sm">
                <Thead position="sticky" top={0} bg="white" zIndex={1}>
                  <Tr>
                    <Th>Nome</Th>
                    <Th>CPF</Th>
                    <Th>Email</Th>
                    <Th>Situação</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {getSelectedPessoas().map((pessoa) => (
                    <Tr key={pessoa.id}>
                      <Td fontWeight="medium">
                        {pessoa.nome || 'Nome não informado'}
                      </Td>
                      <Td fontFamily="mono" fontSize="sm">
                        {pessoa.cpf || 'CPF não informado'}
                      </Td>
                      <Td fontSize="sm" color="gray.600">
                        {pessoa.email || 'Email não informado'}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={pessoa.checkin ? 'green' : 'orange'}
                          size="sm"
                        >
                          {pessoa.checkin ? 'Check-in realizado' : 'Pendente'}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeViewSelected}>
              Fechar
            </Button>
            <Button 
              colorScheme="green" 
              onClick={() => {
                closeViewSelected();
                openBatchDialog();
              }}
              leftIcon={<FiCheckCircle />}
            >
              Processar Estes Registros
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 🆕 Dialog de confirmação para processamento em lote */}
      <AlertDialog
        isOpen={isBatchDialogOpen}
        leastDestructiveRef={confirmRef}
        onClose={closeBatchDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Processar Registros Selecionados
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={3}>
                Você está prestes a processar <strong>{selectedPessoas.size} registro(s)</strong> selecionado(s).
              </Text>
              <Text mb={3} fontSize="sm" color="gray.600">
                Esta ação irá:
              </Text>
              <Box as="ul" pl={4} fontSize="sm" color="gray.600">
                <li>• Imprimir etiqueta para cada registro</li>
                <li>• Realizar check-in automático</li>
                <li>• Atualizar contadores</li>
              </Box>
              <Text mt={3} fontSize="sm" fontWeight="bold" color="orange.600">
                Esta ação não pode ser desfeita.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={confirmRef} onClick={closeBatchDialog}>
                Cancelar
              </Button>
              <Button 
                colorScheme="green" 
                onClick={processBatchCheckin} 
                ml={3}
                isLoading={isBatchProcessing}
                loadingText="Processando..."
              >
                Confirmar Processamento
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Modal de Credencial */}
      <CredentialModal
        isOpen={isCredentialOpen}
        onClose={() => {
          setIsCredentialOpen(false)
          setCredentialPessoa(null)
        }}
        pessoa={credentialPessoa}
      />

      {/* Ficha com os campos que o briefing manda exibir na conferência */}
      <DetalhesParticipante
        pessoa={pessoaDetalhe}
        isOpen={pessoaDetalhe !== null}
        onClose={() => setPessoaDetalhe(null)}
      />
    </>
  )
}

export default ConsultaPanel
